import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { MessageCircleQuestion, X, Send, Loader2, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FloatingHelpChat() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const QUICK_QUESTIONS = t("helpChat.quickQuestions", { returnObjects: true });

  // The greeting must reflect i18n.language, which AuthContext syncs to the
  // account's country only after checkAuth() resolves — that can happen
  // after this component's first render. Re-evaluating in an effect keyed
  // on i18n.language (instead of a one-shot useState initializer) means the
  // greeting is corrected as soon as the real language is known, even for a
  // fresh login. The guard skips overwriting once a real conversation has
  // started, so a later language change can't wipe user messages.
  useEffect(() => {
    setMessages((prev) => {
      const isStillJustGreeting = prev.length === 0 || (prev.length === 1 && prev[0].role === "assistant");
      if (!isStillJustGreeting) return prev;
      return [{ role: "assistant", content: t("helpChat.greeting") }];
    });
  }, [i18n.language, t]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Hide on login page or if not logged in (after all hooks to respect rules-of-hooks)
  if (!user || location.pathname === "/" || location.hash.includes("session_token")) return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    const newHistory = [...messages, { role: "user", content: msg }];
    setMessages(newHistory);
    setInput("");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/help/chat`,
        { message: msg, history: messages.slice(-6) },
        { withCredentials: true, timeout: 40000 });
      setMessages([...newHistory, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      setMessages([...newHistory, { role: "assistant", content: t("helpChat.errorMessage") }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-50 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105"
          style={{
            bottom: "88px", right: "16px",
            width: 58, height: 58,
            background: "var(--gradient-vine)",
            border: "3px solid white",
          }}
          aria-label={t("helpChat.openAriaLabel")}
          data-testid="help-chat-fab"
        >
          <MessageCircleQuestion size={26} color="white" strokeWidth={2.4} />
          <span style={{
            position: "absolute", top: -2, right: -2,
            width: 14, height: 14, borderRadius: "50%",
            background: "var(--gold)", border: "2px solid white",
          }} />
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ background: "rgba(17,20,17,0.4)" }}
          onClick={() => setOpen(false)}>
          <div className="w-full sm:max-w-md vs-card"
            style={{
              borderRadius: "16px 16px 0 0",
              maxHeight: "78vh",
              display: "flex", flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="help-chat-panel">
            {/* Header */}
            <div className="flex items-center gap-2 p-4"
              style={{ background: "var(--gradient-vine)", color: "white", borderRadius: "16px 16px 0 0" }}>
              <Sparkles size={20} color="white" />
              <div className="flex-1">
                <div className="heading font-extrabold">{t("helpChat.headerTitle")}</div>
                <div className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
                  {t("helpChat.headerSubtitle")}
                </div>
              </div>
              <button onClick={() => setOpen(false)}
                className="p-2 rounded-lg"
                style={{ background: "rgba(255,255,255,0.15)", color: "white" }}
                data-testid="help-chat-close">
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef}
              className="flex-1 overflow-y-auto p-3 space-y-2"
              style={{ background: "var(--bg)" }}>
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[85%] p-3 rounded-2xl text-sm"
                    style={{
                      background: m.role === "user" ? "var(--primary)" : "var(--surface)",
                      color: m.role === "user" ? "white" : "var(--text-primary)",
                      border: m.role === "user" ? "none" : "1px solid var(--border)",
                      borderBottomRightRadius: m.role === "user" ? 4 : undefined,
                      borderBottomLeftRadius: m.role === "assistant" ? 4 : undefined,
                      whiteSpace: "pre-wrap",
                    }}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="p-3 rounded-2xl" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                    <Loader2 size={16} className="animate-spin" style={{ color: "var(--primary)" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Quick questions */}
            {messages.length <= 2 && (
              <div className="px-3 pb-2 flex flex-wrap gap-1.5" style={{ background: "var(--bg)" }}>
                {QUICK_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => send(q)}
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ background: "var(--secondary)", border: "1px solid var(--border)" }}
                    data-testid={`quick-q-${i}`}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex gap-2 p-3"
              style={{ background: "var(--surface)", borderTop: "1px solid var(--border)" }}>
              <input
                className="vs-input flex-1"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("helpChat.inputPlaceholder")}
                disabled={loading}
                data-testid="help-chat-input"
              />
              <button type="submit" disabled={loading || !input.trim()}
                className="vs-btn-primary px-4"
                style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
                data-testid="help-chat-send">
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
